import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiSettings, FiPlus, FiX, FiUpload } from 'react-icons/fi';
import { apiGet, apiPut, apiPatch, api } from '@/lib/api';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Drawer } from '@/shared/components/ui/Drawer';
import { toast } from 'sonner';
import { AI_MODELS, CREDIT_CATEGORIES } from '@troia-v3/types';
import type {
  TenantThemeOverrides,
  BaseThemeId,
  IconPackId,
  RadiusScale,
  PageBackgroundOverride,
  CreditCategory,
  CreditCategoryConfig,
  CreditCostEntry,
} from '@troia-v3/types';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface AppSettings {
  id: string;
  name: string;
  domains: string[];
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  status: string;
  baseThemeId?: BaseThemeId;
  themeOverrides?: TenantThemeOverrides;
  createdAt: string;
  updatedAt: string;
}

// CreditCostEntry imported from @troia-v3/types

type SettingsTab = 'config' | 'customization' | 'costs';

// ═══════════════════════════════════════════════════════════════════════════
// Customization Types & Constants
// ═══════════════════════════════════════════════════════════════════════════

interface DraftColors {
  primary: string;
  primaryHover: string;
  success: string;
  warning: string;
  error: string;
}

interface DraftState {
  colors: DraftColors;
  fontFamily: string;
  backgroundType: PageBackgroundOverride['type'];
  backgroundValue: string;
  iconPack: IconPackId | '';
  iconStrokeWidth: string;
  radiusScale: RadiusScale | '';
  borderWidth: string;
  darkModeEnabled: boolean;
  darkColors: DraftColors;
  darkBackgroundType: PageBackgroundOverride['type'];
  darkBackgroundValue: string;
}

const DEFAULT_COLORS: DraftColors = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
};

const DEFAULT_DRAFT: DraftState = {
  colors: { ...DEFAULT_COLORS },
  fontFamily: '',
  backgroundType: undefined,
  backgroundValue: '',
  iconPack: '',
  iconStrokeWidth: '',
  radiusScale: '',
  borderWidth: '',
  darkModeEnabled: false,
  darkColors: { ...DEFAULT_COLORS },
  darkBackgroundType: undefined,
  darkBackgroundValue: '',
};

const BASE_THEME_OPTIONS = [
  { value: 'apple', label: 'Apple' },
  { value: 'current', label: 'Current' },
];

const FONT_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'DM Sans, sans-serif', label: 'DM Sans' },
  { value: 'Nunito, sans-serif', label: 'Nunito' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' },
];

const BG_TYPE_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: 'solid', label: 'Cor solida' },
  { value: 'gradient', label: 'Gradiente' },
  { value: 'image', label: 'Imagem' },
  { value: 'image-overlay', label: 'Imagem com overlay' },
];

const ICON_PACK_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: 'hi2', label: 'Heroicons 2' },
  { value: 'lu', label: 'Lucide' },
  { value: 'fi', label: 'Feather' },
  { value: 'tb', label: 'Tabler' },
];

const ICON_STROKE_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: '1.5', label: '1.5 (fino)' },
  { value: '2', label: '2 (normal)' },
  { value: '2.5', label: '2.5 (grosso)' },
];

const RADIUS_SCALE_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: 'conservative', label: 'Conservador' },
  { value: 'moderate', label: 'Moderado' },
  { value: 'rounded', label: 'Arredondado' },
];

const BORDER_WIDTH_OPTIONS = [
  { value: '', label: 'Padrao do tema' },
  { value: '0.5px', label: '0.5px (fina)' },
  { value: '1px', label: '1px (normal)' },
];

const DARK_MODE_OPTIONS = [
  { value: 'false', label: 'Nao' },
  { value: 'true', label: 'Sim' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Costs Constants (uses shared CREDIT_CATEGORIES from @troia-v3/types)
// ═══════════════════════════════════════════════════════════════════════════

const AI_MODEL_OPTIONS = [
  { value: '', label: 'Todos os modelos' },
  ...AI_MODELS
    .filter((m) => !m.deprecated)
    .map((m) => ({ value: m.id, label: m.name })),
];

const MESSAGING_PROVIDER_OPTIONS = [
  { value: '', label: 'Todos os provedores' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram-messaging', label: 'Instagram' },
  { value: 'facebook-messaging', label: 'Facebook' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'email-resend', label: 'Email' },
];

// Precisa cobrir TODO valor de `CreditUnit` em @troia-v3/types — unidade que
// falta aqui aparece crua na tela ("per_1k_chars"), porque getUnitLabel cai no
// proprio valor. Ficaram de fora quando foram criadas: per_minute e
// per_1k_chars (18/09/2026) e per_gb_month (20/09/2026).
const UNIT_LABELS: Record<string, string> = {
  per_action: 'Por acao',
  per_1k_tokens: 'Por 1k tokens',
  per_month: 'Por mes',
  per_day: 'Por dia',
  per_minute: 'Por minuto',
  per_1k_chars: 'Por 1k caracteres',
  per_gb_month: 'Por GB/mes',
};

const DIRECTION_OPTIONS = [
  { value: '', label: 'Ambos' },
  { value: 'inbound', label: 'Entrada' },
  { value: 'outbound', label: 'Saida' },
];

const getUnitLabel = (unit: string): string => UNIT_LABELS[unit] || unit;

// ═══════════════════════════════════════════════════════════════════════════
// Customization Converters
// ═══════════════════════════════════════════════════════════════════════════

function overridesToDraft(overrides?: TenantThemeOverrides): DraftState {
  if (!overrides) return { ...DEFAULT_DRAFT };

  const c = overrides.colors;
  const dc = overrides.darkMode?.colors;
  const bg = overrides.background?.page;
  const dbg = overrides.darkMode?.background?.page;

  return {
    colors: {
      primary: c?.primary || DEFAULT_COLORS.primary,
      primaryHover: c?.primaryHover || DEFAULT_COLORS.primaryHover,
      success: c?.success || DEFAULT_COLORS.success,
      warning: c?.warning || DEFAULT_COLORS.warning,
      error: c?.error || DEFAULT_COLORS.error,
    },
    fontFamily: overrides.typography?.fontFamily || '',
    backgroundType: bg?.type,
    backgroundValue: bg?.value || '',
    iconPack: overrides.icons?.pack || '',
    iconStrokeWidth: overrides.icons?.strokeWidth?.toString() || '',
    radiusScale: overrides.radius?.scale || '',
    borderWidth: overrides.borders?.width || '',
    darkModeEnabled: overrides.darkMode?.enabled ?? false,
    darkColors: {
      primary: dc?.primary || DEFAULT_COLORS.primary,
      primaryHover: dc?.primaryHover || DEFAULT_COLORS.primaryHover,
      success: dc?.success || DEFAULT_COLORS.success,
      warning: dc?.warning || DEFAULT_COLORS.warning,
      error: dc?.error || DEFAULT_COLORS.error,
    },
    darkBackgroundType: dbg?.type,
    darkBackgroundValue: dbg?.value || '',
  };
}

function draftToOverrides(draft: DraftState): TenantThemeOverrides {
  const overrides: TenantThemeOverrides = {};

  const colorsChanged = Object.entries(draft.colors).some(
    ([k, v]) => v !== DEFAULT_COLORS[k as keyof DraftColors],
  );
  if (colorsChanged) {
    overrides.colors = { ...draft.colors };
  }

  if (draft.fontFamily) {
    overrides.typography = { fontFamily: draft.fontFamily };
  }

  if (draft.backgroundType) {
    overrides.background = {
      page: { type: draft.backgroundType, value: draft.backgroundValue || undefined },
    };
  }

  if (draft.iconPack) {
    overrides.icons = {
      pack: draft.iconPack as IconPackId,
      ...(draft.iconStrokeWidth ? { strokeWidth: parseFloat(draft.iconStrokeWidth) } : {}),
    };
  } else if (draft.iconStrokeWidth) {
    overrides.icons = { strokeWidth: parseFloat(draft.iconStrokeWidth) };
  }

  if (draft.radiusScale) {
    overrides.radius = { scale: draft.radiusScale as RadiusScale };
  }

  if (draft.borderWidth) {
    overrides.borders = { width: draft.borderWidth as '0.5px' | '1px' };
  }

  if (draft.darkModeEnabled) {
    const darkColorsChanged = Object.entries(draft.darkColors).some(
      ([k, v]) => v !== DEFAULT_COLORS[k as keyof DraftColors],
    );

    overrides.darkMode = {
      enabled: true,
      ...(darkColorsChanged ? { colors: { ...draft.darkColors } } : {}),
      ...(draft.darkBackgroundType
        ? {
            background: {
              page: {
                type: draft.darkBackgroundType,
                value: draft.darkBackgroundValue || undefined,
              },
            },
          }
        : {}),
    };
  }

  return overrides;
}

// ═══════════════════════════════════════════════════════════════════════════
// Costs State Types
// ═══════════════════════════════════════════════════════════════════════════

interface ProviderSubEntry {
  providerId: string;
  cost: number;
  direction?: 'inbound' | 'outbound';
}

interface CategoryCostRow {
  category: CreditCategory;
  cost: number;
  direction?: 'inbound' | 'outbound';
  providers: ProviderSubEntry[];
}

/** Convert flat CreditCostEntry[] from server into structured rows */
function serverCostsToRows(entries: CreditCostEntry[]): CategoryCostRow[] {
  const rows: CategoryCostRow[] = [];

  // Initialize all categories
  for (const category of Object.keys(CREDIT_CATEGORIES) as CreditCategory[]) {
    rows.push({ category, cost: 0, direction: undefined, providers: [] });
  }

  // Fill from server data
  for (const entry of entries) {
    const row = rows.find((r) => r.category === entry.category);
    if (!row) continue;

    if (entry.providerId) {
      row.providers.push({
        providerId: entry.providerId,
        cost: entry.cost,
        direction: entry.direction,
      });
    } else {
      row.cost = entry.cost;
      row.direction = entry.direction;
    }
  }

  return rows;
}

/** Convert structured rows back to flat CreditCostEntry[] */
function rowsToServerCosts(rows: CategoryCostRow[]): CreditCostEntry[] {
  const entries: CreditCostEntry[] = [];
  for (const row of rows) {
    const config = CREDIT_CATEGORIES[row.category];
    if (!config) continue;

    // Base entry (always include if cost > 0 or has providers)
    if (row.cost > 0 || row.providers.length === 0) {
      entries.push({
        category: row.category,
        cost: row.cost,
        unit: config.unit,
        ...(config.hasDirection && row.direction ? { direction: row.direction } : {}),
      });
    }

    // Provider-specific entries
    for (const p of row.providers) {
      entries.push({
        category: row.category,
        providerId: p.providerId,
        cost: p.cost,
        unit: config.unit,
        ...(config.hasDirection && p.direction ? { direction: p.direction } : {}),
      });
    }
  }
  return entries;
}

// ═══════════════════════════════════════════════════════════════════════════
// Color Picker Row
// ═══════════════════════════════════════════════════════════════════════════

interface ColorPickerRowProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const ColorPickerRow: React.FC<ColorPickerRowProps> = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-9 h-9 rounded-lg border-0 cursor-pointer p-0"
      style={{ backgroundColor: 'transparent' }}
    />
    <div className="flex-1">
      <FormField label={label}>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
        />
      </FormField>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

export const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>('config');

  // ── Config form state ──────────────────────────────────────────────────
  const [form, setForm] = useState({ name: '', logo: '', favicon: '', domains: [] as string[] });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(async (
    file: File,
    field: 'logo' | 'favicon',
    setUploading: (v: boolean) => void,
  ) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<{ success: boolean; data: { url: string } }>(
        'uploads?folder=settings',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const url = response.data.data.url;
      setForm((f) => ({ ...f, [field]: url }));
    } catch {
      toast.error(`Erro ao fazer upload do ${field === 'logo' ? 'logo' : 'favicon'}`);
    } finally {
      setUploading(false);
    }
  }, []);

  // ── Customization state ────────────────────────────────────────────────
  const [baseThemeId, setBaseThemeId] = useState<BaseThemeId>('apple');
  const [initialBaseThemeId, setInitialBaseThemeId] = useState<BaseThemeId>('apple');
  const [draft, setDraft] = useState<DraftState>({ ...DEFAULT_DRAFT });

  // ── Costs state ────────────────────────────────────────────────────────
  const [costRows, setCostRows] = useState<CategoryCostRow[]>(() => serverCostsToRows([]));

  // ═══════════════════════════════════════════════════════════════════════
  // Queries
  // ═══════════════════════════════════════════════════════════════════════

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => apiGet<AppSettings>('/settings'),
  });

  const { data: serverCosts } = useQuery({
    queryKey: ['admin-costs'],
    queryFn: () => apiGet<CreditCostEntry[]>('/settings/costs'),
  });

  useEffect(() => {
    if (settings) {
      setForm({
        name: settings.name || '',
        logo: settings.logo || '',
        favicon: settings.favicon || '',
        domains: settings.domains || [],
      });
      const themeId = settings.baseThemeId || 'apple';
      setBaseThemeId(themeId);
      setInitialBaseThemeId(themeId);
      setDraft(overridesToDraft(settings.themeOverrides));
    }
  }, [settings]);

  useEffect(() => {
    if (serverCosts) {
      setCostRows(serverCostsToRows(serverCosts));
    }
  }, [serverCosts]);

  // ═══════════════════════════════════════════════════════════════════════
  // Customization updaters
  // ═══════════════════════════════════════════════════════════════════════

  const updateColor = useCallback((key: keyof DraftColors, value: string) => {
    setDraft((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  }, []);

  const updateDarkColor = useCallback((key: keyof DraftColors, value: string) => {
    setDraft((prev) => ({ ...prev, darkColors: { ...prev.darkColors, [key]: value } }));
  }, []);

  const updateField = useCallback(<K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // Costs handlers
  // ═══════════════════════════════════════════════════════════════════════

  const handleCostRowChange = (category: string, field: 'cost' | 'direction', value: number | string) => {
    setCostRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        if (field === 'cost') return { ...r, cost: value as number };
        if (field === 'direction') return { ...r, direction: (value || undefined) as 'inbound' | 'outbound' | undefined };
        return r;
      }),
    );
  };

  const handleAddProvider = (category: CreditCategory) => {
    const config = CREDIT_CATEGORIES[category];
    if (!config?.hasProviderId) return;

    setCostRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        return { ...r, providers: [...r.providers, { providerId: '', cost: 0, direction: undefined }] };
      }),
    );
  };

  const handleRemoveProvider = (category: string, providerIndex: number) => {
    setCostRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        return { ...r, providers: r.providers.filter((_, i) => i !== providerIndex) };
      }),
    );
  };

  const handleProviderChange = (
    category: string,
    providerIndex: number,
    field: 'providerId' | 'cost' | 'direction',
    value: string | number,
  ) => {
    setCostRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        const providers = r.providers.map((p, i) => {
          if (i !== providerIndex) return p;
          if (field === 'cost') return { ...p, cost: value as number };
          if (field === 'providerId') return { ...p, providerId: value as string };
          if (field === 'direction') return { ...p, direction: (value || undefined) as 'inbound' | 'outbound' | undefined };
          return p;
        });
        return { ...r, providers };
      }),
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Mutations
  // ═══════════════════════════════════════════════════════════════════════

  const updateSettingsMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiPut('/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Configuracoes salvas com sucesso');
    },
  });

  const updateCostsMutation = useMutation({
    mutationFn: (data: CreditCostEntry[]) => apiPut<CreditCostEntry[]>('/settings/costs', { costs: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-costs'] });
      toast.success('Custos salvos com sucesso');
    },
  });

  const saveThemeMutation = useMutation({
    mutationFn: async () => {
      const overrides = draftToOverrides(draft);
      await apiPatch('/settings/theme', overrides);
      if (baseThemeId !== initialBaseThemeId) {
        await apiPatch('/settings/base-theme', { baseThemeId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setInitialBaseThemeId(baseThemeId);
      toast.success('Personalizacao salva com sucesso');
    },
    onError: () => {
      toast.error('Erro ao salvar personalizacao');
    },
  });

  const resetThemeMutation = useMutation({
    mutationFn: async () => {
      await apiPatch('/settings/theme', {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setDraft({ ...DEFAULT_DRAFT });
      setBaseThemeId('apple');
      setInitialBaseThemeId('apple');
      toast.success('Personalizacao resetada para o padrao');
    },
    onError: () => {
      toast.error('Erro ao resetar personalizacao');
    },
  });

  // ═══════════════════════════════════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════════════════════════════════

  const handleSaveSettings = () => {
    if (!form.name.trim()) {
      toast.error('Nome do app e obrigatorio');
      return;
    }
    updateSettingsMutation.mutate({
      name: form.name.trim(),
      logo: form.logo.trim() || undefined,
      favicon: form.favicon.trim() || undefined,
      domains: form.domains.filter((d) => d.trim()),
    });
  };

  const handleSaveCosts = () => {
    const flat = rowsToServerCosts(costRows);
    updateCostsMutation.mutate(flat);
  };

  const handleSaveTheme = () => saveThemeMutation.mutate();

  const handleResetTheme = () => {
    if (window.confirm('Tem certeza que deseja resetar todas as personalizacoes?')) {
      resetThemeMutation.mutate();
    }
  };

  const handleOpenDrawer = () => {
    // Reset state from server when opening
    if (serverCosts) setCostRows(serverCostsToRows(serverCosts));
    if (settings) {
      setForm({
        name: settings.name || '',
        logo: settings.logo || '',
        favicon: settings.favicon || '',
        domains: settings.domains || [],
      });
      const themeId = settings.baseThemeId || 'apple';
      setBaseThemeId(themeId);
      setInitialBaseThemeId(themeId);
      setDraft(overridesToDraft(settings.themeOverrides));
    }
    setActiveTab('config');
    setDrawerOpen(true);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════════════════════════════════

  const getCategoryLabel = (category: CreditCategory): string =>
    CREDIT_CATEGORIES[category]?.label || category;

  const getProviderOptions = (config: CreditCategoryConfig) => {
    if (config.providerType === 'ai_model') return AI_MODEL_OPTIONS;
    if (config.providerType === 'messaging') return MESSAGING_PROVIDER_OPTIONS;
    return [];
  };

  const isMutating =
    updateSettingsMutation.isPending ||
    updateCostsMutation.isPending ||
    saveThemeMutation.isPending ||
    resetThemeMutation.isPending;

  // ═══════════════════════════════════════════════════════════════════════
  // Tab definitions
  // ═══════════════════════════════════════════════════════════════════════

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'config', label: 'Configuracao' },
    { key: 'customization', label: 'Personalizacao' },
    { key: 'costs', label: 'Custos' },
  ];

  // ═══════════════════════════════════════════════════════════════════════
  // Footer per tab
  // ═══════════════════════════════════════════════════════════════════════

  const renderFooter = () => {
    if (activeTab === 'config') {
      return (
        <>
          <Button variant="ghost" onClick={() => setDrawerOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveSettings} loading={updateSettingsMutation.isPending}>
            Salvar Configuracoes
          </Button>
        </>
      );
    }

    if (activeTab === 'customization') {
      return (
        <>
          <Button variant="ghost" onClick={handleResetTheme} loading={resetThemeMutation.isPending}>
            Resetar
          </Button>
          <Button onClick={handleSaveTheme} loading={saveThemeMutation.isPending}>
            Salvar Personalizacao
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="ghost" onClick={() => setDrawerOpen(false)}>
          Cancelar
        </Button>
        <Button onClick={handleSaveCosts} loading={updateCostsMutation.isPending}>
          Salvar Custos
        </Button>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
            Configuracoes
          </h1>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Configuracoes gerais do app
          </p>
        </div>
        <Button onClick={handleOpenDrawer}>
          <FiSettings className="w-4 h-4 mr-2" />
          Editar
        </Button>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────────── */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* App Info Card */}
          <Card>
            <CardHeader className="pb-2 py-3 px-4">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Informacoes do App
              </h2>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {settings?.logo && (
                    <img
                      src={settings.logo}
                      alt="Logo"
                      className="h-10 w-10 rounded-lg object-cover border"
                      style={{ borderColor: 'var(--theme-card-border)' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {settings?.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                      {settings?.status}
                    </p>
                  </div>
                </div>
                {settings?.domains && settings.domains.length > 0 && (
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>Dominios:</p>
                    {settings.domains.map((d, i) => (
                      <span
                        key={i}
                        className="inline-block px-2 py-0.5 rounded text-xs mr-1 mb-1"
                        style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-card-text)' }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Costs Preview Card */}
          <Card>
            <CardHeader className="pb-2 py-3 px-4">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Tabela de Custos
              </h2>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {serverCosts && serverCosts.length > 0 ? (
                <div className="space-y-1">
                  {serverCosts.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>
                        {getCategoryLabel(c.category)}
                        {c.direction ? ` (${c.direction === 'inbound' ? 'entrada' : 'saida'})` : ''}
                        {c.providerId ? ` - ${c.providerId}` : ''}
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
                        {c.cost} cr / {getUnitLabel(c.unit)}
                      </span>
                    </div>
                  ))}
                  {serverCosts.length > 6 && (
                    <p className="text-xs pt-1" style={{ color: 'var(--theme-muted-text)' }}>
                      +{serverCosts.length - 6} itens
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhum custo configurado
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
           Drawer
         ═════════════════════════════════════════════════════════════════ */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size="lg"
        icon={<FiSettings className="w-5 h-5" />}
        title="Configuracoes"
        subtitle="Configuracoes gerais, personalizacao e custos"
        disabled={isMutating}
        footer={renderFooter()}
      >
        {/* ── Tab Navigation (full-width, flush with drawer edges) ──────── */}
        <div className="-mx-6 -mt-5 flex px-6 gap-1 border-b mb-5" style={{ borderColor: '#e5e7eb' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px"
              style={{
                color: activeTab === tab.key ? '#8b5cf6' : '#64748b',
                borderColor: activeTab === tab.key ? '#8b5cf6' : 'transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════
             Tab 1: Configuracao
           ═════════════════════════════════════════════════════════════ */}
        {activeTab === 'config' && (
          <div className="space-y-4">
            <FormField label="Nome do App" required>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome do seu app"
              />
            </FormField>

            <FormField label="Logo">
              <div className="flex items-center gap-3">
                {form.logo ? (
                  <img
                    src={form.logo}
                    alt="Logo"
                    className="h-12 w-12 rounded-lg object-cover border shrink-0"
                    style={{ borderColor: '#e5e7eb' }}
                    onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                  />
                ) : (
                  <div
                    className="h-12 w-12 rounded-lg border flex items-center justify-center shrink-0"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}
                  >
                    <FiUpload className="w-4 h-4" style={{ color: '#94a3b8' }} />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    loading={uploadingLogo}
                  >
                    {form.logo ? 'Alterar' : 'Upload'}
                  </Button>
                  {form.logo && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm((f) => ({ ...f, logo: '' }))}
                    >
                      Remover
                    </Button>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'logo', setUploadingLogo);
                    e.target.value = '';
                  }}
                />
              </div>
            </FormField>

            <FormField label="Favicon">
              <div className="flex items-center gap-3">
                {form.favicon ? (
                  <img
                    src={form.favicon}
                    alt="Favicon"
                    className="h-12 w-12 rounded-lg object-cover border shrink-0"
                    style={{ borderColor: '#e5e7eb' }}
                    onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                  />
                ) : (
                  <div
                    className="h-12 w-12 rounded-lg border flex items-center justify-center shrink-0"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}
                  >
                    <FiUpload className="w-4 h-4" style={{ color: '#94a3b8' }} />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => faviconInputRef.current?.click()}
                    loading={uploadingFavicon}
                  >
                    {form.favicon ? 'Alterar' : 'Upload'}
                  </Button>
                  {form.favicon && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm((f) => ({ ...f, favicon: '' }))}
                    >
                      Remover
                    </Button>
                  )}
                </div>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'favicon', setUploadingFavicon);
                    e.target.value = '';
                  }}
                />
              </div>
            </FormField>

            <div>
              <label className="text-sm font-medium mb-2 block" style={{ color: '#1e293b' }}>
                Domínios
              </label>
              <div className="space-y-2">
                {form.domains.map((domain, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={domain}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          domains: f.domains.map((d, idx) => (idx === i ? e.target.value : d)),
                        }))
                      }
                      placeholder="app.example.com"
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          domains: f.domains.filter((_, idx) => idx !== i),
                        }))
                      }
                      className="p-2 rounded-lg transition-colors hover:bg-red-50"
                      style={{ color: '#ef4444' }}
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, domains: [...f.domains, ''] }))}
                  className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: '#8b5cf6' }}
                >
                  <FiPlus className="w-4 h-4" />
                  Adicionar domínio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
             Tab 2: Personalizacao
           ═════════════════════════════════════════════════════════════ */}
        {activeTab === 'customization' && (
          <div className="space-y-6">
            {/* Tema Base */}
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Tema Base</h3>
              <div className="max-w-xs">
                <FormField label="Base do tema" description="Selecione o tema base do app">
                  <Select
                    options={BASE_THEME_OPTIONS}
                    value={baseThemeId}
                    onChange={(v) => setBaseThemeId(v as BaseThemeId)}
                  />
                </FormField>
              </div>
            </div>

            {/* Cores */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Cores</h3>
              <div className="space-y-3 max-w-sm">
                <ColorPickerRow label="Primaria" value={draft.colors.primary} onChange={(v) => updateColor('primary', v)} />
                <ColorPickerRow label="Primaria Hover" value={draft.colors.primaryHover} onChange={(v) => updateColor('primaryHover', v)} />
                <ColorPickerRow label="Sucesso" value={draft.colors.success} onChange={(v) => updateColor('success', v)} />
                <ColorPickerRow label="Aviso" value={draft.colors.warning} onChange={(v) => updateColor('warning', v)} />
                <ColorPickerRow label="Erro" value={draft.colors.error} onChange={(v) => updateColor('error', v)} />
              </div>
              {/* Mini color preview */}
              <div className="mt-4 flex gap-2">
                {Object.entries(draft.colors).map(([key, color]) => (
                  <div key={key} className="text-center">
                    <div
                      className="w-10 h-10 rounded-lg border"
                      style={{ backgroundColor: color, borderColor: '#e5e7eb' }}
                    />
                    <span className="text-[10px] mt-1 block" style={{ color: '#6b7280' }}>{key}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tipografia */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Tipografia</h3>
              <div className="max-w-xs">
                <FormField label="Familia da fonte">
                  <Select
                    options={FONT_OPTIONS}
                    value={draft.fontFamily}
                    onChange={(v) => updateField('fontFamily', v)}
                  />
                </FormField>
                {draft.fontFamily && (
                  <p className="mt-3 text-sm" style={{ fontFamily: draft.fontFamily, color: '#334155' }}>
                    Texto de exemplo com a fonte selecionada.
                  </p>
                )}
              </div>
            </div>

            {/* Fundo da Pagina */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Fundo da Pagina</h3>
              <div className="space-y-3 max-w-sm">
                <FormField label="Tipo de fundo">
                  <Select
                    options={BG_TYPE_OPTIONS}
                    value={draft.backgroundType || ''}
                    onChange={(v) =>
                      updateField('backgroundType', (v || undefined) as PageBackgroundOverride['type'])
                    }
                  />
                </FormField>
                {draft.backgroundType && (
                  <FormField
                    label="Valor"
                    description={
                      draft.backgroundType === 'solid'
                        ? 'Cor hex (ex: #f5f5f5)'
                        : draft.backgroundType === 'gradient'
                          ? 'CSS gradient (ex: linear-gradient(135deg, #667eea, #764ba2))'
                          : 'URL da imagem'
                    }
                  >
                    <Input
                      value={draft.backgroundValue}
                      onChange={(e) => updateField('backgroundValue', e.target.value)}
                      placeholder={
                        draft.backgroundType === 'solid'
                          ? '#f5f5f5'
                          : draft.backgroundType === 'gradient'
                            ? 'linear-gradient(135deg, #667eea, #764ba2)'
                            : 'https://...'
                      }
                    />
                  </FormField>
                )}
              </div>
            </div>

            {/* Icones */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Icones</h3>
              <div className="space-y-3 max-w-xs">
                <FormField label="Pack de icones">
                  <Select
                    options={ICON_PACK_OPTIONS}
                    value={draft.iconPack}
                    onChange={(v) => updateField('iconPack', v as IconPackId | '')}
                  />
                </FormField>
                <FormField label="Espessura do traco">
                  <Select
                    options={ICON_STROKE_OPTIONS}
                    value={draft.iconStrokeWidth}
                    onChange={(v) => updateField('iconStrokeWidth', v)}
                  />
                </FormField>
              </div>
            </div>

            {/* Bordas */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Bordas</h3>
              <div className="space-y-3 max-w-xs">
                <FormField label="Escala de arredondamento">
                  <Select
                    options={RADIUS_SCALE_OPTIONS}
                    value={draft.radiusScale}
                    onChange={(v) => updateField('radiusScale', v as RadiusScale | '')}
                  />
                </FormField>
                <FormField label="Espessura da borda">
                  <Select
                    options={BORDER_WIDTH_OPTIONS}
                    value={draft.borderWidth}
                    onChange={(v) => updateField('borderWidth', v)}
                  />
                </FormField>
              </div>
            </div>

            {/* Dark Mode */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Dark Mode</h3>
              <div className="max-w-xs">
                <FormField label="Habilitar Dark Mode">
                  <Select
                    options={DARK_MODE_OPTIONS}
                    value={draft.darkModeEnabled ? 'true' : 'false'}
                    onChange={(v) => updateField('darkModeEnabled', v === 'true')}
                  />
                </FormField>
              </div>

              {draft.darkModeEnabled && (
                <>
                  <div className="border-t pt-4 mt-4" style={{ borderColor: '#e5e7eb' }}>
                    <p className="text-xs font-medium mb-3" style={{ color: '#6b7280' }}>
                      Cores do modo escuro
                    </p>
                    <div className="space-y-3 max-w-sm">
                      <ColorPickerRow label="Primaria" value={draft.darkColors.primary} onChange={(v) => updateDarkColor('primary', v)} />
                      <ColorPickerRow label="Primaria Hover" value={draft.darkColors.primaryHover} onChange={(v) => updateDarkColor('primaryHover', v)} />
                      <ColorPickerRow label="Sucesso" value={draft.darkColors.success} onChange={(v) => updateDarkColor('success', v)} />
                      <ColorPickerRow label="Aviso" value={draft.darkColors.warning} onChange={(v) => updateDarkColor('warning', v)} />
                      <ColorPickerRow label="Erro" value={draft.darkColors.error} onChange={(v) => updateDarkColor('error', v)} />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4" style={{ borderColor: '#e5e7eb' }}>
                    <p className="text-xs font-medium mb-3" style={{ color: '#6b7280' }}>
                      Fundo no modo escuro
                    </p>
                    <div className="space-y-3 max-w-sm">
                      <FormField label="Tipo de fundo">
                        <Select
                          options={BG_TYPE_OPTIONS}
                          value={draft.darkBackgroundType || ''}
                          onChange={(v) =>
                            updateField('darkBackgroundType', (v || undefined) as PageBackgroundOverride['type'])
                          }
                        />
                      </FormField>
                      {draft.darkBackgroundType && (
                        <FormField label="Valor">
                          <Input
                            value={draft.darkBackgroundValue}
                            onChange={(e) => updateField('darkBackgroundValue', e.target.value)}
                            placeholder={
                              draft.darkBackgroundType === 'solid'
                                ? '#1a1a2e'
                                : draft.darkBackgroundType === 'gradient'
                                  ? 'linear-gradient(135deg, #1a1a2e, #16213e)'
                                  : 'https://...'
                            }
                          />
                        </FormField>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Preview */}
            <div
              className="border-t pt-5"
              style={{ borderColor: '#e5e7eb' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Preview</h3>
              <div
                className="rounded-lg border p-4"
                style={{
                  borderColor: '#e5e7eb',
                  backgroundColor:
                    draft.backgroundType === 'solid' && draft.backgroundValue
                      ? draft.backgroundValue
                      : '#f9fafb',
                  ...(draft.backgroundType === 'gradient' && draft.backgroundValue
                    ? { background: draft.backgroundValue }
                    : {}),
                  fontFamily: draft.fontFamily || undefined,
                }}
              >
                <div
                  className="rounded-lg border p-3 mb-3"
                  style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#e5e7eb',
                    borderRadius:
                      draft.radiusScale === 'conservative'
                        ? '4px'
                        : draft.radiusScale === 'rounded'
                          ? '12px'
                          : '8px',
                    borderWidth: draft.borderWidth || '1px',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: draft.colors.primary }} />
                    <div>
                      <div className="text-xs font-semibold" style={{ color: '#111827' }}>
                        Card de exemplo
                      </div>
                      <div className="text-[10px]" style={{ color: '#6b7280' }}>
                        Subtitulo descritivo
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div
                      className="h-6 px-2 rounded text-[10px] font-medium flex items-center"
                      style={{
                        backgroundColor: draft.colors.primary,
                        color: '#ffffff',
                        borderRadius:
                          draft.radiusScale === 'conservative'
                            ? '4px'
                            : draft.radiusScale === 'rounded'
                              ? '8px'
                              : '6px',
                      }}
                    >
                      Primario
                    </div>
                    <div
                      className="h-6 px-2 rounded text-[10px] font-medium flex items-center"
                      style={{ backgroundColor: draft.colors.success, color: '#ffffff' }}
                    >
                      Sucesso
                    </div>
                    <div
                      className="h-6 px-2 rounded text-[10px] font-medium flex items-center"
                      style={{ backgroundColor: draft.colors.warning, color: '#ffffff' }}
                    >
                      Aviso
                    </div>
                    <div
                      className="h-6 px-2 rounded text-[10px] font-medium flex items-center"
                      style={{ backgroundColor: draft.colors.error, color: '#ffffff' }}
                    >
                      Erro
                    </div>
                  </div>
                </div>
                {draft.fontFamily && (
                  <p className="text-xs" style={{ color: '#6b7280', fontFamily: draft.fontFamily }}>
                    Fonte: {draft.fontFamily.split(',')[0]}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
             Tab 3: Custos
           ═════════════════════════════════════════════════════════════ */}
        {activeTab === 'costs' && (
          <div className="space-y-1">
            <p className="text-sm mb-4" style={{ color: '#64748b' }}>
              Configure o custo em creditos de cada operacao
            </p>

            {costRows.map((row) => {
              const config = CREDIT_CATEGORIES[row.category];
              if (!config) return null;

              return (
                <div
                  key={row.category}
                  className="border rounded-lg overflow-hidden mb-3"
                  style={{ borderColor: '#e5e7eb' }}
                >
                  {/* Category base row */}
                  <div
                    className="px-3 py-2.5 flex items-center gap-3"
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    {/* Label */}
                    <div className="min-w-[180px]">
                      <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                        {config.label}
                      </span>
                      <span
                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
                      >
                        {getUnitLabel(config.unit)}
                      </span>
                    </div>

                    {/* Cost input */}
                    <div className="w-24">
                      <Input
                        type="number"
                        value={String(row.cost)}
                        onChange={(e) =>
                          handleCostRowChange(row.category, 'cost', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0"
                      />
                    </div>

                    {/* Direction select (if applicable) */}
                    {config.hasDirection && (
                      <div className="w-28">
                        <Select
                          options={DIRECTION_OPTIONS}
                          value={row.direction || ''}
                          onChange={(val) => handleCostRowChange(row.category, 'direction', val)}
                        />
                      </div>
                    )}

                    {/* Add provider button */}
                    {config.hasProviderId && (
                      <button
                        type="button"
                        onClick={() => handleAddProvider(row.category)}
                        className="ml-auto p-1.5 rounded-lg transition-colors"
                        style={{ color: '#8b5cf6' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3e8ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Adicionar custo por provedor"
                      >
                        <FiPlus className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Provider sub-entries */}
                  {row.providers.length > 0 && (
                    <div style={{ borderTop: '1px solid #e5e7eb' }}>
                      {row.providers.map((provider, pIdx) => (
                        <div
                          key={pIdx}
                          className="px-3 py-2 flex items-center gap-3"
                          style={{
                            backgroundColor: '#ffffff',
                            borderTop: pIdx > 0 ? '1px solid #f1f5f9' : undefined,
                          }}
                        >
                          {/* Indent indicator */}
                          <div className="w-4 flex justify-center" style={{ color: '#d1d5db' }}>
                            <span className="text-xs">└</span>
                          </div>

                          {/* Provider select */}
                          <div className="min-w-[160px]">
                            <Select
                              options={getProviderOptions(config)}
                              value={provider.providerId}
                              onChange={(val) =>
                                handleProviderChange(row.category, pIdx, 'providerId', val)
                              }
                            />
                          </div>

                          {/* Cost */}
                          <div className="w-24">
                            <Input
                              type="number"
                              value={String(provider.cost)}
                              onChange={(e) =>
                                handleProviderChange(
                                  row.category,
                                  pIdx,
                                  'cost',
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              placeholder="0"
                            />
                          </div>

                          {/* Direction (if applicable) */}
                          {config.hasDirection && (
                            <div className="w-28">
                              <Select
                                options={DIRECTION_OPTIONS}
                                value={provider.direction || ''}
                                onChange={(val) =>
                                  handleProviderChange(row.category, pIdx, 'direction', val)
                                }
                              />
                            </div>
                          )}

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveProvider(row.category, pIdx)}
                            className="p-1.5 rounded-lg transition-colors ml-auto"
                            style={{ color: '#94a3b8' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#fef2f2';
                              e.currentTarget.style.color = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#94a3b8';
                            }}
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};
